import type { useRive } from '@rive-app/react-canvas';
import { useEffect, useRef } from 'react';

// The `.riv` file drives the collapse/expand look with a `collapsed`
// boolean and a `transition` number (0 = fully collapsed, 100 = fully expanded) on the
// ViewModelInstance. The two properties are sequenced, not just set together, because the
// artwork itself only actually *looks* right partway through each direction if `collapsed`
// flips at the right end of the sequence rather than the start:
// - Collapsing: flip `collapsed` to `true` immediately, then animate `transition` down to 0 -
//   the collapsed look is what's *revealed* by that animation, so it needs to already be the
//   active look before `transition` starts moving.
// - Expanding: animate `transition` back up to 100 first, and only flip `collapsed` to `false`
//   once that finishes - flipping it immediately would jump straight to the expanded look
//   instead of animating there.
// Rive doesn't tween a ViewModel-bound number for us when we set `.value` directly - it snaps
// the artwork straight to whatever value we last wrote - so this hook tweens `transition`
// itself across frames.
const FULLY_COLLAPSED_TRANSITION_VALUE = 0;
const FULLY_EXPANDED_TRANSITION_VALUE = 100;
const TRANSITION_TWEEN_DURATION_MS = 100;

type Rive = ReturnType<typeof useRive>['rive'];
type ViewModelInstanceNumber = NonNullable<
  ReturnType<NonNullable<NonNullable<Rive>['viewModelInstance']>['number']>
>;

/**
 * @param resyncToken Re-applies the current `isCollapsed` state whenever this value changes,
 * even if `isCollapsed` itself didn't - something inside the `.riv` file's own state machine
 * can otherwise reset the collapsed/expanded artwork back to its default drawing on
 * interactions this hook doesn't control, leaving it out of sync with `isCollapsed`. Pass
 * something that changes on every SunnyChat mode transition (e.g. `sunnyChat.mode`).
 */
export function useCollapseTrigger(rive: Rive, isCollapsed: boolean, resyncToken?: unknown) {
  const isFirstResyncCheckRef = useRef(true);
  const lastResyncTokenRef = useRef(resyncToken);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const resyncTokenChanged = !isFirstResyncCheckRef.current && lastResyncTokenRef.current !== resyncToken;
    isFirstResyncCheckRef.current = false;
    lastResyncTokenRef.current = resyncToken;

    const collapsedProperty = rive?.viewModelInstance?.boolean('collapsed');
    const transitionProperty = rive?.viewModelInstance?.number('transition');
    if (!collapsedProperty || !transitionProperty) {
      return;
    }

    const targetTransitionValue = isCollapsed ? FULLY_COLLAPSED_TRANSITION_VALUE : FULLY_EXPANDED_TRANSITION_VALUE;
    // Both properties are the real, currently-applied state (not separately tracked flags that
    // could drift from them) - so this can compare against them directly instead of needing its
    // own "did we already apply this" bookkeeping.
    if (
      collapsedProperty.value === isCollapsed &&
      transitionProperty.value === targetTransitionValue &&
      !resyncTokenChanged
    ) {
      return;
    }

    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }

    if (isCollapsed) {
      // Collapsing: the collapsed look needs to already be active before `transition` starts
      // animating into it (see this file's own top comment).
      collapsedProperty.value = true;
      tweenTransition(transitionProperty, targetTransitionValue, animationFrameRef);
    } else {
      // Expanding: animate back to the expanded look first, and only flip `collapsed` once
      // that finishes.
      tweenTransition(transitionProperty, targetTransitionValue, animationFrameRef, () => {
        collapsedProperty.value = false;
      });
    }
  }, [rive, isCollapsed, resyncToken]);

  useEffect(
    () => () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    [],
  );
}

function tweenTransition(
  transitionProperty: ViewModelInstanceNumber,
  targetValue: number,
  animationFrameRef: { current: number | undefined },
  onComplete?: () => void,
) {
  const startValue = transitionProperty.value;
  const startTime = performance.now();

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / TRANSITION_TWEEN_DURATION_MS, 1);
    transitionProperty.value = startValue + (targetValue - startValue) * progress;
    if (progress < 1) {
      animationFrameRef.current = requestAnimationFrame(step);
    } else {
      animationFrameRef.current = undefined;
      onComplete?.();
    }
  };
  animationFrameRef.current = requestAnimationFrame(step);
}
